import { useCollaboration } from '../../../../collaboration/CollaborationProvider';
import {
    type GroupElementRequestAPI,
    getGroupElement as getGroupElementAPI,
    saveGroupElement as saveGroupElementAPI,
} from '../../../../sources/graph';
import { useNotificationDispatch } from '../../../notifications/NotificationProvider';

type Props = {
    flowId: string;
    setIsLoading: (isLoading: boolean) => void;
    refreshFlow: () => void;
    focusAndSelectElement: (id: string) => void;
};

const useGroup = ({ flowId, setIsLoading, refreshFlow, focusAndSelectElement }: Props) => {
    const { invoke } = useCollaboration();
    const dispatchNotification = useNotificationDispatch();

    const getGroupElement = async (id: string) => {
        try {
            return await getGroupElementAPI(id, flowId);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        }

        return undefined;
    };

    const saveGroupElement = async (groupElement: GroupElementRequestAPI) => {
        try {
            // Validate that all the selected users/groups have an attribute selected
            if (
                (groupElement.authorization?.groups &&
                    (groupElement.authorization.groups.some((item) => !item.authenticationId) ||
                        groupElement.authorization.groups.some((item) => !item.attribute))) ||
                (groupElement.authorization?.users &&
                    (groupElement.authorization.users.some((item) => !item.authenticationId) ||
                        groupElement.authorization.users.some((item) => !item.attribute)))
            ) {
                throw new Error('Please select items from all of the dropdowns');
            }

            if (!groupElement.id) {
                groupElement.id = crypto.randomUUID();
            }

            setIsLoading(true);

            const updatedGroupElement = await saveGroupElementAPI(groupElement, flowId);

            invoke('GraphChanged', flowId, [updatedGroupElement.id], null);

            refreshFlow();
            focusAndSelectElement(updatedGroupElement.id);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    return {
        getGroupElement,
        saveGroupElement,
    };
};

export default useGroup;
